import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import {
  BaseReportComponentProps,
  HideByAssetClassPerformance,
  HidingOptionsRequest,
  MdaReportTypeField,
  MdaReportTypes,
  PerformanceMethodology,
  PeriodBaseRequest,
} from '../../store/types';
import { ReportGroupingOption } from '../ReportGroupingOption';
import { MultiplePeriodSelector } from '../MultiplePeriodSelector';

export interface AssetClassPerformanceProps extends BaseReportComponentProps {
  setAssetClassPerformanceHidingOptionParameter: ActionCreatorWithPayload<HidingOptionsRequest<HideByAssetClassPerformance>, string>;
  setAssetClassPerformanceMethodology: ActionCreatorWithPayload<PerformanceMethodology, string>;
  expandAssetClassPerformance: ActionCreatorWithoutPayload<string>;
  setAssetClassPerformanceSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeAssetClassPerformanceParameter: ActionCreatorWithPayload<DateRange, string>;
  setAssetClassPerformanceMultiplePeriods: ActionCreatorWithPayload<PeriodBaseRequest[], string>;
}

export const AssetClassPerformance = ({
  setAssetClassPerformanceHidingOptionParameter,
  setDateRangeAssetClassPerformanceParameter,
  setAssetClassPerformanceSelectedParameter,
  setAssetClassPerformanceMethodology,
  setAssetClassPerformanceMultiplePeriods,
  expandAssetClassPerformance,
  isDatesLessThanFullYear,
  createReportSortRequest,
  setGroupConsolidatedOption,
  parameters,
}: AssetClassPerformanceProps) => {
  const handleClickDateForAssetClassPerformance = (dates: DateRange) => {
    setDateRangeAssetClassPerformanceParameter(dates);
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(dates)) {
      setAssetClassPerformanceHidingOptionParameter({
        option: HideByAssetClassPerformance.AnnualisedReturns,
        value: false,
      });
    }
  };

  const handleClickAssetClassPerformanceSelect = () => {
    setAssetClassPerformanceSelectedParameter(!parameters.selectedReports?.assetClassPerformance.selected);
    createReportSortRequest(
      !parameters.selectedReports?.assetClassPerformance.selected,
      MdaReportTypes.AssetClassPerformance.id,
      MdaReportTypes.AssetClassPerformance.displayName
    );
  };

  const dateRange: DateRange = {
    dateFrom: parameters?.selectedReports?.assetClassPerformance?.fromDate,
    dateTo: parameters?.selectedReports?.assetClassPerformance?.toDate,
    dateRangeType: parameters?.selectedReports?.assetClassPerformance?.dateRangeType,
  };

  useEffect(() => {
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(parameters.dates)) {
      setAssetClassPerformanceHidingOptionParameter({
        option: HideByAssetClassPerformance.AnnualisedReturns,
        value: false,
      });
    }
  }, [parameters.dates]);

  return (
    <Accordion onChange={expandAssetClassPerformance} expanded={parameters.selectedReports?.assetClassPerformance.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          aria-label="Acknowledge"
          sx={{ flex: 1 }}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.assetClassPerformance.selected}
              onChangeHandler={handleClickAssetClassPerformanceSelect}
            />
          }
          label="Asset Class Performance"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker inceptionDate={parameters.inceptionDate} dateRange={dateRange} setDateRange={handleClickDateForAssetClassPerformance} />
        </div>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container direction="column" gap={1}>
          <Grid item>
            <MultiplePeriodSelector
              maxPeriods={7}
              disabled={!parameters.selectedReports.assetClassPerformance.includeReturnsSummary}
              disableInceptionRemoval={true}
              onChange={(periods: PeriodBaseRequest[]) => {
                setAssetClassPerformanceMultiplePeriods(periods);
              }}
              values={parameters.selectedReports.assetClassPerformance.multiPeriods || []}
            />
          </Grid>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
              <WO2Select
                labelId="performance-methodology"
                label="performance methodology"
                onChange={(e) => setAssetClassPerformanceMethodology(e.target.value as PerformanceMethodology)}
                value={parameters.selectedReports.assetClassPerformance.performanceMethodology}
                defaultValue={PerformanceMethodology.TimeWeighted}
              >
                <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
                <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>

          <Grid item container direction="row" alignItems="center" spacing={1} mt={1}>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.FrankingCredits,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.excludeFrankingCredits}
                color="primary"
              />
              <Typography variant="h4">Exclude Franking Credits</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.AnnualisedReturns,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.showAnnualisedReturns}
                color="primary"
                disabled={isDatesLessThanFullYear && isDatesLessThanFullYear(dateRange)}
              />
              <Typography variant="h4">Show Annualised Returns</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.ExclusionList,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.applyExclusionList}
                color="primary"
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.CapitalIncomeReturns,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.excludeCapitalIncomeReturns}
                color="primary"
              />
              <Typography variant="h4">Exclude Capital/Income Returns</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.AllocationChange,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.includeAllocationChange}
                color="primary"
              />
              <Typography variant="h4">Include Allocation Change</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.ReturnsSummary,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.includeReturnsSummary}
                color="primary"
              />
              <Typography variant="h4">Include Returns Summary</Typography>
            </Grid>
            <Grid item container xs={4} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setAssetClassPerformanceHidingOptionParameter({
                    value: e.target.checked,
                    option: HideByAssetClassPerformance.BenchmarkComparison,
                  })
                }
                checked={parameters.selectedReports.assetClassPerformance.includeBenchmarkComparison}
                color="primary"
              />
              <Typography variant="h4">Include Benchmark Comparison</Typography>
            </Grid>
          </Grid>
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column" mt={2}>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.assetClassPerformance.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.AssetClassPerformance}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
